import { css } from '@emotion/react'
import { isEmptyDocument, isLink } from 'datocms-structured-text-utils'
import { Link, graphql } from 'gatsby'
import { ComponentPropsWithoutRef, type ElementType } from 'react'
import { renderNodeRule } from 'react-datocms/structured-text'

import { DatoStructuredText } from '@/features/common'
import { mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { bodyHeading } from '../../styles/sharedStyles'

type TableData = {
  columns: string[]
  data: { [key: string]: string }[]
}
interface Props extends ComponentPropsWithoutRef<'table'> {
  data: Queries.TableFragment | null | undefined
  headingLevel?: number
  variant: 'SECTION' | 'NESTED'
}

export const Table = ({
  data,
  headingLevel = 3,
  variant,
  ...props
}: Props) => {
  const Heading = `h${headingLevel}` as ElementType
  const table = JSON.parse(
    data?.table as unknown as string
  ) as TableData
  const styles = {
    container: css`
      width: 100%;
      box-sizing: border-box;
      margin: 2em 0 0;
      display: grid;
      ${variant === 'SECTION' &&
      css`
        padding: 0 var(--margin);
        font-size: var(--fs-18);
      `}
    `,
    heading: css`
      font-size: var(--fs-36);
      font-family: var(--ff-display);
      text-transform: none;
      color: ${colors.gray20};
      margin: 0.25em 0 0.75em;
      ${variant === 'SECTION' &&
      css`
        ${bodyHeading}
      `}
    `,
    tableWrapper: css`
      max-width: 100%;
      overflow: scroll;
    `,
    table: css`
      width: 100%;
      min-width: fit-content;
      display: grid;
      grid-template-columns: repeat(${table.columns.length}, auto);
      thead,
      tbody {
        display: contents;
      }
      tr {
        display: contents;
        text-align: left;
      }
      th,
      td {
        display: flex;
        align-items: flex-start;
        padding: 2em;
        &:not(:first-of-type) {
          text-align: left;
          justify-content: flex-start;
        }
        ${mq().s} {
          padding: 1em;
        }
        &:nth-of-type(even) {
          background: rgba(0, 0, 0, 0.07);
        }
        &:nth-of-type(odd) {
          background: rgba(0, 0, 0, 0.03);
        }
        &[data-long='true'] {
          min-width: 24ch;
        }
      }
      thead {
        th {
          border-bottom: 2px solid ${colors.red};
          padding-bottom: 1em;
          align-items: flex-end;
        }
      }
      tbody {
        td {
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          &:nth-of-type(1) {
            font-weight: 550;
          }
        }
        tr:last-of-type {
          td {
            border-bottom: none;
          }
        }
      }
    `,
    footnote: css`
      font-size: max(83.3%, var(--fs-15));
      margin: 2em 0 0;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      > div {
        max-width: 100ch;
        > :last-child {
          margin-bottom: 0;
        }
      }
      a {
        color: var(--link-color, ${colors.red});
        @media (hover: hover) {
          &:hover {
            color: var(--link-color-hover, ${colors.redDark});
          }
        }
      }
    `,
  }
  return (
    <div css={styles.container}>
      {data?.heading && (
        <Heading css={styles.heading}>{data?.heading}</Heading>
      )}
      <div css={styles.tableWrapper}>
        <table
          css={styles.table}
          {...props}
        >
          <thead>
            <tr>
              {table?.columns?.map((heading, i) => (
                <th key={i}>{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table?.data.map((row, i) => (
              <tr key={i}>
                {Object.values(row).map((value, ii) => (
                  <td
                    data-type={table?.columns?.[ii]}
                    data-long={value.length > 30}
                    key={ii}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!isEmptyDocument(data?.footnote) && (
        <div css={styles.footnote}>
          <div>
            <DatoStructuredText
              customNodeRules={[
                renderNodeRule(isLink, ({ node, children, key }) => {
                  const isInternal = /^\/(?!\/)/.test(node.url)
                  if (isInternal) {
                    return (
                      <Link
                        to={node.url}
                        key={key}
                        {...node.meta}
                      >
                        {children}
                      </Link>
                    )
                  } else
                    return (
                      <a
                        key={key}
                        href={node.url}
                        target="_blank"
                        rel="noreferrer"
                        {...node.meta}
                      >
                        {children}
                      </a>
                    )
                }),
              ]}
              data={data?.footnote}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export const TableFragment = graphql`
  fragment Table on DatoCmsTable {
    __typename
    id: originalId
    heading
    table
    footnote {
      value
    }
  }
`
